import { useState } from 'react';
import { z } from 'zod';
import { T, useGT } from 'gt-react';
import { toast } from 'sonner';
import type { SalesOrderV3Detail } from '@erp/rpc';
import { CheckCircleIcon, WarningIcon } from '@phosphor-icons/react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import AssociateProductV2 from '@/components/products/AssociateProductV2';
import { Button } from 'src/components/ui/button';
import { cn } from '@/lib/utils';
import { useMatchDraftSalesOrderLineItem } from '@/api/rpc/hooks/accounts-receivable/useDraftSalesOrders';
import { formatCurrency } from 'src/pages/authenticated/sales-orders/components/line-item-table/priceUtils';

type LineItem = SalesOrderV3Detail['lineItems'][number];

/**
 * The `additionalData` keys the match popover reads. The bag is untyped on the
 * wire; `.catch` per key keeps one malformed value from discarding the rest.
 */
const LabelledCodeSchema = z.object({
  label: z.string().nullish(),
  value: z.string(),
});

const MatchAdditionalDataSchema = z.object({
  sku: z.string().nullish().catch(null),
  upc: z.string().nullish().catch(null),
  itemName: z.string().nullish().catch(null),
  suggestedProductId: z.string().nullish().catch(null),
  topProductVariantMatches: z
    .array(z.object({ productVariantId: z.string(), confidence: z.number() }))
    .nullish()
    .catch(null),
  /** Other codes the document printed for the line ("Market Name" …). */
  alternateIdentifiers: z.array(LabelledCodeSchema).nullish().catch(null),
  /** The code the automatic match was made on, when it was not the primary. */
  matchedIdentifier: LabelledCodeSchema.extend({
    layer: z.string().nullish(),
  })
    .nullish()
    .catch(null),
});

function parseMatchAdditionalData(additionalData: LineItem['additionalData']) {
  const parsed = MatchAdditionalDataSchema.safeParse(additionalData ?? {});
  return parsed.success ? parsed.data : MatchAdditionalDataSchema.parse({});
}

/**
 * The review-page "Match" column cell: a matched/unmatched status icon that
 * opens a popover with the raw customer request and the shared product→variant
 * match flow (`AssociateProductV2`). When the draft isn't editable (submitted /
 * rejected) the popover still opens for viewing, but the picker is inert and
 * the commit row is replaced by a locked note. The v3 popover counterpart of
 * v2's `SalesOrderDraftAssociateLineItemPopover`.
 */
export function LineMatchPopover({
  draftId,
  lineItem,
  customerId,
  currency,
  editable,
}: {
  draftId: string;
  lineItem: LineItem;
  customerId?: string | null;
  currency?: string | null;
  editable: boolean;
}) {
  const t = useGT();
  const [open, setOpen] = useState(false);
  // Variant picked in the popover but not yet committed — the match only
  // persists when the user confirms with the "Match product" button.
  const [pendingVariantId, setPendingVariantId] = useState<string | null>(null);
  const matchLineItem = useMatchDraftSalesOrderLineItem(draftId);

  const matched =
    !!lineItem.selectedCustomerProductId || !!lineItem.selectedProductVariantId;
  const extras = parseMatchAdditionalData(lineItem.additionalData);
  const identifier = lineItem.customerIdentifier || extras.sku || extras.upc;
  const description = lineItem.customerDescription || extras.itemName;
  const alternateCodes = (extras.alternateIdentifiers ?? []).filter(
    (code) => code.value && code.value !== identifier,
  );
  const matchedOnAlternate =
    extras.matchedIdentifier && extras.matchedIdentifier.value !== identifier
      ? extras.matchedIdentifier
      : null;

  const icon = matched ? (
    <CheckCircleIcon className="h-4 w-4 flex-shrink-0 text-green-600" />
  ) : (
    <WarningIcon className="h-4 w-4 flex-shrink-0 text-amber-500" />
  );

  const currentVariantId = lineItem.selectedProductVariantId ?? null;
  // Committable only when a variant is picked and it differs from the current
  // match — re-confirming the same variant would just churn discount/approval
  // side effects for nothing.
  const canCommit =
    !!pendingVariantId &&
    pendingVariantId !== currentVariantId &&
    !matchLineItem.isPending;

  function handleOpenChange(next: boolean) {
    setOpen(next);
    // Discard any uncommitted selection when the popover closes.
    if (!next) setPendingVariantId(null);
  }

  function handleCommit() {
    if (!pendingVariantId) return;
    matchLineItem.mutate(
      { lineItemId: lineItem.id, productVariantId: pendingVariantId },
      {
        onSuccess: () => handleOpenChange(false),
        onError: () => toast.error('Could not match the line item'),
      },
    );
  }

  return (
    // `modal` so the nested autocomplete's own popover receives keystrokes.
    <Popover modal open={open} onOpenChange={handleOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="mx-auto flex cursor-pointer items-center justify-center rounded p-1 hover:bg-muted"
            >
              {icon}
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>
          {editable
            ? matched
              ? t('Item is matched. Click to change.')
              : t('Click to match this item to a product.')
            : matched
              ? t('Item is matched. Click to view.', {
                  context: 'locked sales order line match tooltip',
                })
              : t('Item is not matched to a product. Click to view.', {
                  context: 'locked sales order line match tooltip',
                })}
        </TooltipContent>
      </Tooltip>
      <PopoverContent
        side="right"
        align="start"
        className="max-h-[70vh] w-[560px] overflow-y-auto"
      >
        {/* Locked drafts: the picker stays readable but takes no input; the
            sticky Customer Request block above it is rendered by the picker,
            so the guard wraps both — text stays legible either way. `inert`
            also blocks keyboard focus, which pointer-events alone does not. */}
        <div
          className={cn(!editable && 'pointer-events-none opacity-70')}
          inert={!editable}
          aria-disabled={!editable}
        >
          <AssociateProductV2
            selectedProductVariantId={
              pendingVariantId ?? currentVariantId ?? undefined
            }
            selectedProductId={extras.suggestedProductId ?? undefined}
            topProductVariantMatches={
              extras.topProductVariantMatches ?? undefined
            }
            customerId={customerId ?? undefined}
            onVariantSelected={({ productVariantId }) =>
              setPendingVariantId(productVariantId ?? null)
            }
          >
            {/* Sticky customer-request context (the raw extracted line). */}
            <div className="sticky top-0 z-10 border-b border-border bg-background pb-2">
              <p className="mb-1 text-sm font-medium">
                <T>Customer Request</T>
              </p>
              <div className="grid grid-cols-[1fr_2fr_auto] gap-2 rounded border border-border p-2 text-xs">
                <div>
                  <div className="font-medium text-muted-foreground">
                    <T>ID</T>
                  </div>
                  <div className="break-words">{identifier || '—'}</div>
                  {alternateCodes.map((code) => (
                    <div
                      key={`${code.label ?? ''}:${code.value}`}
                      className={cn(
                        'break-words text-muted-foreground',
                        matchedOnAlternate?.value === code.value &&
                          'font-medium text-foreground',
                      )}
                      title={
                        matchedOnAlternate?.value === code.value
                          ? t('Matched on this code', {
                              context:
                                'tooltip: the product match used this alternate code',
                            })
                          : undefined
                      }
                    >
                      {code.label ? `${code.label}: ${code.value}` : code.value}
                    </div>
                  ))}
                </div>
                <div>
                  <div className="font-medium text-muted-foreground">
                    <T>Item</T>
                  </div>
                  <div className="break-words">
                    {description || t('No description available')}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-medium text-muted-foreground">
                    <T>Unit Price</T>
                  </div>
                  <div className="tabular-nums">
                    {formatCurrency(lineItem.unitPrice, currency)}
                  </div>
                </div>
              </div>
            </div>
          </AssociateProductV2>
        </div>
        {!editable ? (
          <p className="mt-3 border-t border-border pt-3 text-xs text-muted-foreground">
            <T context="sales order line match popover, draft not editable">
              Matching is locked for submitted or rejected orders
            </T>
          </p>
        ) : (
          /* Explicit commit: picking a variant above only stages it — the match
            persists (and side effects fire) only on confirmation. */
          <div className="mt-3 flex items-center justify-end gap-2 border-t border-border pt-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => handleOpenChange(false)}
            >
              <T>Cancel</T>
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={!canCommit}
              onClick={handleCommit}
            >
              {matchLineItem.isPending ? (
                <T>Matching…</T>
              ) : pendingVariantId && pendingVariantId === currentVariantId ? (
                <T>Already matched</T>
              ) : (
                <T>Match product</T>
              )}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
